import shuffle from 'lodash/shuffle';
import striptags from 'striptags';
import Utils from './utils';
import { ITEM_SUBTYPES, ITEM_TYPES } from '../components/types/objectTypes';
import { PAGING_TYPES } from '../components/types/paging';
import { extensionMap } from '../components/fields/videoQuestion/common/constants';
import {
  isMaxDiffExperiment,
  isDecorator,
  isPageTitle,
  isConceptTest,
  isConjointExperiment,
  isConjoint2Experiment,
} from '../components/utils/helpers';

class SurveyUtils {
  static expandContactInfoAndConjointExperimentItems(pageItems) {
    const items = {};
    Object.entries(pageItems).map(([key, item]) => {
      items[key] = item;
      if (item.type === ITEM_TYPES.contactInfo) {
        const {
          countryMultipleChoiceId,
          countryMultipleChoiceQuestionId,
          stateMultipleChoiceId,
          stateMultipleChoiceQuestionId,
        } = item.config;
        if (countryMultipleChoiceId) {
          items[countryMultipleChoiceId] = { _id: countryMultipleChoiceQuestionId };
        }
        if (stateMultipleChoiceId) {
          items[stateMultipleChoiceId] = { _id: stateMultipleChoiceQuestionId };
        }
      }
      if (isConjoint2Experiment(item)) {
        items[item.originalFrontendId] = item;
      }
    });
    return items;
  }

  static convertFrontendIdToIds(responses, pageItems) {
    const items = this.expandContactInfoAndConjointExperimentItems(pageItems);
    return Object.keys(responses).reduce((answers, frontendId) => {
      if (!responses[frontendId] || frontendId.includes('_')) {
        return answers;
      }
      const item = items[frontendId];
      if (!item || item.isEmbeddedVariable) {
        return answers;
      }
      if (isMaxDiffExperiment(item)) {
        const { index: experimentSetIndex } = item.experiment || {};
        const setValues = {};

        for (let i = 0; i <= experimentSetIndex; i++) {
          const setExperimentFrontendId = `${item.frontendId}_${i}`;
          if (responses[setExperimentFrontendId] && responses[setExperimentFrontendId].values) {
            setValues[i] = responses[setExperimentFrontendId];
          }
        }

        return {
          ...answers,
          [item._id]: {
            aggregated: { ...responses[frontendId] },
            setValues,
          },
        };
      }

      return {
        ...answers,
        [items[frontendId]._id]: responses[frontendId],
      };
    }, {});
  }

  static addQuestionsExtraProps(currentNumber = 0, items = [], areExperimentsOnSamePage = false) {
    let counter = currentNumber;
    let conceptCounter = 0;
    for (const item of items) {
      item.config = item.config || {};
      if (isConceptTest(item)) {
        item.questionNumberForSurvey = conceptCounter++;
      }
      if (item.isEmbeddedVariable || isDecorator(item) || isPageTitle(item)) {
        continue;
      }
      item.questionNumberForSurvey = ++counter;
      if (areExperimentsOnSamePage && (isConjointExperiment(item) || isMaxDiffExperiment(item))) {
        SurveyUtils.addExperimentIndexToItemFrontendId(item);
      }
      if (isConjoint2Experiment(item)) {
        SurveyUtils.addIndexForExperimentToItemFrontendId(item);
      }
    }
  }

  static isPageAConceptTest(page) {
    if (!page.items || page.items === 0) {
      return false;
    }

    const [firstItem, secondItem] = page.items;
    return isConceptTest(firstItem) || (isPageTitle(firstItem) && isConceptTest(secondItem));
  }

  // TODO: Remove unnecessary code from below
  static addPage({
    title,
    pageCount,
    paging,
    pageItems,
    item,
    transformedPages,
    pageId,
    pageFrontendId,
    conceptTestId,
    experimentId,
    conceptTestDecorator,
    currentPageIndex,
    isFirstItemInBlock,
    shouldHideConceptTestDecorator,
    isLastItemInBlock,
    pageConfig = {},
    initialPageCount,
  }) {
    if (experimentId) {
      const itemIndex = pageItems.findIndex((frontendId) => frontendId === item.frontendId);
      const isFirstItemInPage = itemIndex === 0;
      for (let i = 0; i < item.config.numberOfSets; i++) {
        if (i === 0 && isFirstItemInPage && currentPageIndex === 0 && paging === PAGING_TYPES.none) {
          continue;
        }
        pageCount++;
        transformedPages.push({
          experimentId,
          pageFrontendId,
          pageId,
          pageNumber: pageCount,
          items: [],
          isFirstItemInBlock,
        });
      }
      return pageCount;
    }
    if (paging === PAGING_TYPES.question) {
      pageCount++;
      const newPage = {
        title,
        pageNumber: pageCount,
        items: [],
        pageFrontendId,
        pageId,
        conceptTestId,
        isFirstItemInBlock,
      };
      if (pageConfig.loopingEnabled) {
        newPage.loopingEnabled = pageConfig.loopingEnabled;
        newPage.loopingStartingPage = initialPageCount + 1;
        if (isFirstItemInBlock && pageConfig.loopingEnabled) {
          newPage.loopingConfig = pageConfig.loopingConfig;
        }
        if (isLastItemInBlock && pageConfig.loopingEnabled) {
          newPage.returnToPageNumber = initialPageCount + 1;
        }
      }
      if (conceptTestDecorator !== undefined) {
        newPage.shouldHideConceptTestDecorator = shouldHideConceptTestDecorator;
        newPage.items.push(conceptTestDecorator);
      }
      transformedPages.push(newPage);
      return pageCount;
    }
    if (paging === PAGING_TYPES.none) {
      if (
        item.type === ITEM_TYPES.decorator &&
        [ITEM_SUBTYPES.conceptTest, ITEM_SUBTYPES.link].includes(item.subtype)
      ) {
        pageCount++;
        transformedPages.push({
          pageId,
          pageNumber: pageCount,
          items: [],
        });
      }
      return pageCount;
    }
    return pageCount;
  }

  static getPagesForSurvey({ pages, items, paging, pagesIndex }) {
    const transformedPages =
      paging !== PAGING_TYPES.none
        ? []
        : pagesIndex.length > 0 &&
          pages[pagesIndex[0]] &&
          pages[pagesIndex[0]].items &&
          pages[pagesIndex[0]].items[0] &&
          items[pages[pagesIndex[0]].items[0]] &&
          items[pages[pagesIndex[0]].items[0]].type === ITEM_TYPES.decorator &&
          [ITEM_SUBTYPES.link, ITEM_SUBTYPES.conceptTest].includes(items[pages[pagesIndex[0]].items[0]].subtype)
        ? []
        : [
            {
              pageNumber: 1,
              items: [],
            },
          ];
    let pageCount = transformedPages.length;
    let lastPage;
    let lastPageWithTitle;

    pagesIndex.forEach((pageFrontendId, index) => {
      let experimentPagesWereAdded = false;
      const page = pages[pageFrontendId];
      if (paging === PAGING_TYPES.none && transformedPages.length && striptags(page.title) !== '' && index === 0) {
        transformedPages[0].title = page.title;
        transformedPages[0].pageFrontendId = page.frontendId;
        lastPageWithTitle = page.frontendId;
      }
      const lastPageIsACT =
        lastPage &&
        lastPage.items &&
        lastPage.items[0] &&
        items[lastPage.items[0]] &&
        items[lastPage.items[0]].type === ITEM_TYPES.decorator &&
        [ITEM_SUBTYPES.conceptTest, ITEM_SUBTYPES.link].includes(items[lastPage.items[0]].subtype);
      const currentPageIsACT =
        page &&
        page.items &&
        page.items.length > 0 &&
        items[page.items[0]] &&
        items[page.items[0]].type === ITEM_TYPES.decorator &&
        [ITEM_SUBTYPES.conceptTest, ITEM_SUBTYPES.link].includes(items[page.items[0]].subtype);
      const isMaxDiffExperimentPage =
        page &&
        page.items &&
        page.items.length > 0 &&
        items[page.items[0]] &&
        items[page.items[0]].type === ITEM_TYPES.grid &&
        items[page.items[0]].subtype === ITEM_SUBTYPES.maxDiffExperiment;
      const isConjointExperimentPage =
        page &&
        page.items &&
        page.items.length > 0 &&
        items[page.items[0]] &&
        items[page.items[0]].type === ITEM_TYPES.conjoint;
      const currentPageIsExperimentSet =
        page &&
        page.items &&
        page.items.length > 0 &&
        items[page.items[0]] &&
        (isMaxDiffExperimentPage || isConjointExperimentPage);
      if (
        (paging === PAGING_TYPES.page && !currentPageIsExperimentSet) ||
        (paging === PAGING_TYPES.none && lastPageIsACT && !currentPageIsACT && !currentPageIsExperimentSet)
      ) {
        pageCount++;
        transformedPages.push({
          pageNumber: pageCount,
          items: [],
          title: !currentPageIsACT && page.title ? page.title : null,
          pageFrontendId: page.frontendId,
        });
        lastPageWithTitle = page.frontendId;
      }

      let sectionBreakTitle = page.title && page.title.length > 0;
      const lastPosition = lastPageIsACT
        ? 0
        : transformedPages[pageCount - 1]
        ? transformedPages[pageCount - 1].items.length
        : 0;
      const lastPageWithoutExperimentPages = lastPageIsACT ? pageCount : pageCount - 1;
      const conceptTestDecorator = currentPageIsACT ? page.items[0] : undefined;
      const shouldHideConceptTestDecorator = currentPageIsACT ? items[page.items[0]].config.hideLinkAfterShow : true;
      const pageConfig = page.config;
      const initialPageCount = pageCount;
      page.items.forEach((itemFrontendId, itemIndex) => {
        if (paging === PAGING_TYPES.question && currentPageIsACT && itemIndex === 0 && page.items.length > 1) {
          return;
        }
        const item = items[itemFrontendId];
        if (item.config && item.config.rowPerPage) {
          if (item.rows) {
            let rowsIndexes = Object.keys(item.rows);
            if (item.config.randomize) {
              rowsIndexes = shuffle(rowsIndexes);
            }
            rowsIndexes.forEach((rowsIndex) => {
              pageCount = this.addPage({
                conceptTestDecorator: itemIndex === 0 ? undefined : conceptTestDecorator,
                shouldHideConceptTestDecorator: itemIndex > 1 && shouldHideConceptTestDecorator,
                title: !currentPageIsACT && sectionBreakTitle ? page.title : null,
                pageCount,
                paging,
                pageItems: page.items,
                item,
                transformedPages,
                pageId: page.frontendId,
                pageFrontendId: !currentPageIsACT ? page.frontendId : undefined,
                currentPageIndex: index,
                conceptTestId: currentPageIsACT ? items[page.items[0]].conceptTestId : undefined,
                experimentId: undefined,
                isFirstItemInBlock: !currentPageIsACT
                  ? itemIndex === 0
                  : page.items.length > 1
                  ? itemIndex === 1
                  : itemIndex === 0,
              });
              transformedPages[pageCount - 1].items.push(`${item.frontendId}::${parseInt(rowsIndex, 10) + 1}`);
            });
          }
          return;
        }
        const notHideOnSurvey = !item.config || !item.config.hideOnSurvey;
        if (notHideOnSurvey) {
          const isMaxDiffExperiment = item.type === ITEM_TYPES.grid && item.subtype === ITEM_SUBTYPES.maxDiffExperiment;
          const isConjointExperiment = item.type === ITEM_TYPES.conjoint;
          const isExperiment = isMaxDiffExperiment || isConjointExperiment;
          pageCount = this.addPage({
            conceptTestDecorator: itemIndex === 0 ? undefined : conceptTestDecorator,
            shouldHideConceptTestDecorator: itemIndex > 1 && shouldHideConceptTestDecorator,
            title: !currentPageIsACT && sectionBreakTitle ? page.title : null,
            pageCount,
            paging,
            pageItems: page.items,
            item,
            transformedPages,
            pageId: page.frontendId,
            pageFrontendId: !currentPageIsACT ? page.frontendId : undefined,
            currentPageIndex: index,
            conceptTestId: currentPageIsACT ? items[page.items[0]].conceptTestId : undefined,
            experimentId: isExperiment ? item.frontendId : undefined,
            isFirstItemInBlock: !currentPageIsACT
              ? itemIndex === 0
              : page.items.length > 1
              ? itemIndex === 1
              : itemIndex === 0,
            isLastItemInBlock: page.items.length - 1 === itemIndex,
            pageConfig,
            initialPageCount,
          });
          if (isExperiment) {
            const pageIndex = pageCount - item.config.numberOfSets > 0 ? pageCount - item.config.numberOfSets : 0;
            if (transformedPages[pageIndex]) {
              transformedPages[pageIndex].items.push(item.frontendId);
            }
            experimentPagesWereAdded = true;
          } else {
            transformedPages[pageCount - 1].items.push(item.frontendId);
          }
        }
        if (sectionBreakTitle && paging === PAGING_TYPES.question) {
          sectionBreakTitle = false;
        }
      });
      if (pageConfig && pageConfig.loopingEnabled && transformedPages[transformedPages.length - 1]) {
        transformedPages[transformedPages.length - 1].returnToPageNumber = initialPageCount + 1;
      }
      const isPageBreakTitleAvailable = !!page.title && !!striptags(page.title).length;
      if (
        paging === PAGING_TYPES.none &&
        !currentPageIsACT &&
        isPageBreakTitleAvailable &&
        lastPageWithTitle !== page.frontendId
      ) {
        const pageTitleIndex = experimentPagesWereAdded ? lastPageWithoutExperimentPages : pageCount - 1;
        const length = transformedPages[pageTitleIndex].items.length;
        transformedPages[pageTitleIndex].items = [
          ...transformedPages[pageTitleIndex].items.slice(0, lastPosition),
          pageFrontendId,
          ...transformedPages[pageTitleIndex].items.slice(lastPosition, length),
        ];
      }

      lastPage = page;
    });
    return transformedPages;
  }

  static getSurveyInitialPageNumber(project = {}) {
    const { survey = {} } = project;
    const { welcomeText, termsAndConditions } = survey;
    const hasWelcomeText = welcomeText && striptags(welcomeText).trim() !== '';
    const hasTermsAndConditions = termsAndConditions && striptags(termsAndConditions).trim() !== '';

    if (hasWelcomeText || hasTermsAndConditions) {
      return 0;
    }

    return 1;
  }

  static mapCurrentPageToOnePageItemPosition(pagePosition, pages, onePage) {
    if (pagePosition > 0) {
      const itemFrontendId = Utils.parseItemFrontendId(pages[pagePosition - 1].items[0]);
      return onePage.findIndex((item) => item.frontendId === itemFrontendId) + 1;
    }
    return -1;
  }

  static isEmptyPage(page = {}) {
    return !page.items || (page.items && page.items.length === 0 && page.title === undefined);
  }

  static randomizeConcepts({ pages, items, pagesIndex }) {
    const newPagesIndex = [];
    let lastConceptTestId;
    let pagesToShuffle = [];

    pagesIndex.forEach((itemFrontendId) => {
      const page = pages[itemFrontendId];
      if (!page.items || page.items.length === 0) {
        if (pagesToShuffle.length > 0) {
          newPagesIndex.push(...shuffle(pagesToShuffle));
        }
        pagesToShuffle = [];
        lastConceptTestId = undefined;
        newPagesIndex.push(page.frontendId);
        return;
      }
      const firstItem = items[page.items[0]];
      const pageIsConceptTest =
        firstItem.type === ITEM_TYPES.decorator && firstItem.subtype === ITEM_SUBTYPES.conceptTest;
      if (!pageIsConceptTest) {
        lastConceptTestId = undefined;
      }
      if (!pageIsConceptTest || !firstItem.randomizeConcepts) {
        if (pagesToShuffle.length > 0) {
          newPagesIndex.push(...shuffle(pagesToShuffle));
        }
        newPagesIndex.push(page.frontendId);
        pagesToShuffle = [];
        return;
      }
      if (firstItem.conceptTestId !== lastConceptTestId) {
        lastConceptTestId = firstItem.conceptTestId;
        if (pagesToShuffle.length > 0) {
          newPagesIndex.push(...shuffle(pagesToShuffle));
        }
        pagesToShuffle = [];
      }
      pagesToShuffle.push(page.frontendId);
    });

    if (pagesIndex.length > newPagesIndex.length && pagesToShuffle.length > 0) {
      newPagesIndex.push(...shuffle(pagesToShuffle));
    }

    return newPagesIndex;
  }

  static randomizePageItems(pages) {
    for (const page of pages) {
      if (!page.randomizeItems || !page.items || !page.items.length) {
        continue;
      }
      if (page.items[0].subtype === ITEM_SUBTYPES.conceptTest) {
        // for concept tests, the decorator needs to be always the first item of the page
        page.items = [page.items[0], ...shuffle(page.items.slice(1))];
      } else {
        page.items = shuffle(page.items);
      }
      let itemPosition = 0;
      page.items = page.items.map((item) => {
        item.position = ++itemPosition;
        return item;
      });
    }
  }

  static reorderConceptTestsDisplayed({ project, pages, items, pagesIndex }) {
    if (!project || !project.survey || !project.survey.pages) {
      return;
    }
    const newConceptTestsToDisplay = {};

    pagesIndex.forEach((pageFrontendId) => {
      const page = pages[pageFrontendId];
      const firstItemFrontendId = page.items && page.items[0];
      if (!firstItemFrontendId) {
        return;
      }
      const firstItem = items[firstItemFrontendId];
      const { conceptTestId } = firstItem;
      if (!conceptTestId) {
        return;
      }
      if (newConceptTestsToDisplay[conceptTestId] === undefined) {
        newConceptTestsToDisplay[conceptTestId] = [];
      }
      newConceptTestsToDisplay[conceptTestId].push(firstItem._id);
    });
    project.conceptTestsToDisplay = newConceptTestsToDisplay;
  }

  static getLastLoopResponses(responses) {
    if (!responses) {
      return {};
    }

    const latestResponses = {};
    for (let responseId of Object.keys(responses)) {
      const response = responses[responseId];
      if (Array.isArray(response)) {
        const lastResponse = responses[responseId][responses[responseId].length - 1];
        if (lastResponse) {
          latestResponses[responseId] = lastResponse;
        }
      } else {
        latestResponses[responseId] = response;
      }
    }

    return latestResponses;
  }

  static addExperimentIndexToItemFrontendId(item) {
    const { experiment: { index } = {}, _id, frontendId } = item;
    if (index !== undefined) {
      item.frontendId = `${frontendId}_${index}`;
      item.originalFrontendId = frontendId;
      item._id = `${_id}_${index}`;
    }
  }

  static addIndexForExperimentToItemFrontendId(item) {
    const { indexForExperiment, frontendId } = item;
    if (indexForExperiment !== undefined) {
      item.frontendId = `${frontendId}_${indexForExperiment}`;
      item.originalFrontendId = frontendId;
    }
  }

  static formatVideoQuestionAnswers(answers, pastVideoQuestionIds) {
    const formattedAnswers = {};
    Object.keys(answers).forEach((key) => {
      const answer = answers[key];
      if (!('permission' in answer) && !('isSubmissionDeclined' in answer)) {
        formattedAnswers[key] = answer;
        return;
      }
      if (!pastVideoQuestionIds.includes(key)) {
        // to avoid sending the video answer if the user is not on that page
        return;
      }
      if ((answer.value || []).length > 0 && !answer.isSubmissionDeclined) {
        const extension = extensionMap[answer.mimeType];
        formattedAnswers[key] = { value: 'upload-file', mimeType: answer.mimeType, extension }; // when there is a video stored
        return;
      }
      let reason = ['granted', 'prompt'].includes(answer.permission) ? 'empty' : answer.permission;
      if (answer.isSubmissionDeclined) {
        // when user does not want to submit a video
        reason = 'disagree';
      }
      formattedAnswers[key] = { value: 'empty', reasons: [reason] }; // when there is no value or it is an empty array
    });
    return formattedAnswers;
  }
}

export default SurveyUtils;
